import { calcPositionRisk, calcPositionRiskPercent } from './tradeCalculations'
import type { YahooDailyCandle } from './yahooHistoricalChart'

export type TradeChartPointKind = 'entry' | 'eod' | 'current' | 'exit'

export type TradeChartPoint = {
  at: string
  time: number
  price: number
  kind: TradeChartPointKind
  dailyChangePct: number | null
  changeFromPriorPct: number | null
}

export type TradeDailyCandle = {
  time: number
  open: number
  high: number
  low: number
  close: number
  source: 'yahoo' | 'history' | 'synthetic' | 'filled'
}

export type TradeHistoryAnnotation = {
  id?: string
  time: number
  price: number | null
  title: string
  description: string
  eventType: string
}

export type PositionRiskPoint = {
  time: number
  positionSize: number
  riskPct: number | null
  riskAmount: number | null
  quantity: number
  avgCost: number
  stopLoss: number
}

export type TradeChartDayRow = TradeDailyCandle & {
  positionSize: number | null
  riskPct: number | null
  annotations: TradeHistoryAnnotation[]
}

type TradeForChart = {
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  exit_date: string | null
  exit_price: number | null
  long_short: 'long' | 'short'
  quantity: number | null
  contract_size: number | null
  position_size: number | null
  current_price?: number | null
  current_price_updated_at?: string | null
  daily_change_percentage?: number | null
}

export type HistoryForChart = {
  id?: string
  date: string
  title?: string
  description?: string
  price?: number
  tag?: string
  eventType?: string
  quantity?: number | null
  autoGenerated?: boolean
  metadata?: {
    daily_change_pct?: number
    stop_loss?: number
    new_stop_loss?: number
    [key: string]: unknown
  }
}

function parseTime(iso: string): number {
  const t = Date.parse(iso)
  return Number.isFinite(t) ? t : Date.now()
}

function startOfUtcDay(ms: number): number {
  const d = new Date(ms)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

function dayKey(ms: number): string {
  return new Date(startOfUtcDay(ms)).toISOString().slice(0, 10)
}

export function buildTradeChartPoints(
  trade: TradeForChart,
  history: HistoryForChart[],
  averageCost: number
): TradeChartPoint[] {
  const raw: Omit<TradeChartPoint, 'changeFromPriorPct'>[] = []

  const entryAt =
    trade.entry_date ||
    trade.exit_date ||
    new Date().toISOString()

  raw.push({
    at: new Date(parseTime(entryAt)).toISOString(),
    time: parseTime(entryAt),
    price: averageCost,
    kind: 'entry',
    dailyChangePct: null,
  })

  const closed = Boolean(trade.exit_date && trade.exit_price != null && Number.isFinite(trade.exit_price))
  const exitTimeCutoff =
    closed && trade.exit_date ? parseTime(trade.exit_date) : null

  const marketEvents = history
    .filter(
      (e) =>
        (e.tag === 'market_data' || e.eventType === 'market_data') &&
        e.price !== undefined &&
        Number.isFinite(e.price)
    )
    .filter((e) => exitTimeCutoff === null || parseTime(e.date) <= exitTimeCutoff)
    .map((e) => {
      const md = e.metadata
      const pct =
        md && typeof md.daily_change_pct === 'number' && Number.isFinite(md.daily_change_pct)
          ? md.daily_change_pct
          : null
      return {
        at: new Date(parseTime(e.date)).toISOString(),
        time: parseTime(e.date),
        price: e.price as number,
        kind: 'eod' as const,
        dailyChangePct: pct,
      }
    })

  raw.push(...marketEvents)

  if (!closed && trade.current_price != null && Number.isFinite(trade.current_price)) {
    const at =
      trade.current_price_updated_at ||
      new Date().toISOString()
    raw.push({
      at: new Date(parseTime(at)).toISOString(),
      time: parseTime(at),
      price: trade.current_price,
      kind: 'current',
      dailyChangePct:
        trade.daily_change_percentage != null && Number.isFinite(trade.daily_change_percentage)
          ? trade.daily_change_percentage
          : null,
    })
  }

  if (closed && trade.exit_date && trade.exit_price != null) {
    raw.push({
      at: new Date(parseTime(trade.exit_date)).toISOString(),
      time: parseTime(trade.exit_date),
      price: trade.exit_price,
      kind: 'exit',
      dailyChangePct: null,
    })
  }

  raw.sort((a, b) => a.time - b.time)

  const deduped: Omit<TradeChartPoint, 'changeFromPriorPct'>[] = []
  for (const p of raw) {
    const last = deduped[deduped.length - 1]
    if (last && last.time === p.time) {
      deduped[deduped.length - 1] = {
        ...last,
        ...p,
        dailyChangePct: p.dailyChangePct ?? last.dailyChangePct,
        price: p.price,
        kind: p.kind === 'exit' || p.kind === 'current' ? p.kind : last.kind,
      }
      continue
    }
    deduped.push(p)
  }

  return deduped.map((p, i) => {
    let changeFromPriorPct: number | null = null
    if (i > 0) {
      const prev = deduped[i - 1].price
      if (prev !== 0 && Number.isFinite(prev)) {
        changeFromPriorPct = ((p.price - prev) / prev) * 100
      }
    }
    return { ...p, changeFromPriorPct }
  })
}

export function buildDailyCandlesFromMarketHistory(
  trade: TradeForChart,
  history: HistoryForChart[],
  averageCost: number
): TradeDailyCandle[] {
  const byDay = new Map<string, { open: number; high: number; low: number; close: number; time: number }>()

  const marketPoints = history
    .filter(
      (e) =>
        (e.tag === 'market_data' || e.eventType === 'market_data') &&
        e.price != null &&
        Number.isFinite(e.price)
    )
    .sort((a, b) => parseTime(a.date) - parseTime(b.date))

  for (const e of marketPoints) {
    const time = startOfUtcDay(parseTime(e.date))
    const key = dayKey(time)
    const price = e.price as number
    const existing = byDay.get(key)
    if (!existing) {
      byDay.set(key, { time, open: price, high: price, low: price, close: price })
    } else {
      existing.high = Math.max(existing.high, price)
      existing.low = Math.min(existing.low, price)
      existing.close = price
    }
  }

  const entryTime = trade.entry_date ? startOfUtcDay(parseTime(trade.entry_date)) : null
  if (entryTime != null) {
    const key = dayKey(entryTime)
    const price = averageCost
    const existing = byDay.get(key)
    if (!existing) {
      byDay.set(key, { time: entryTime, open: price, high: price, low: price, close: price })
    } else {
      existing.open = price
      existing.high = Math.max(existing.high, price)
      existing.low = Math.min(existing.low, price)
    }
  }

  if (trade.exit_date && trade.exit_price != null) {
    const time = startOfUtcDay(parseTime(trade.exit_date))
    const key = dayKey(time)
    const price = trade.exit_price
    const existing = byDay.get(key)
    if (!existing) {
      byDay.set(key, { time, open: price, high: price, low: price, close: price })
    } else {
      existing.close = price
      existing.high = Math.max(existing.high, price)
      existing.low = Math.min(existing.low, price)
    }
  }

  return Array.from(byDay.values())
    .sort((a, b) => a.time - b.time)
    .map((c) => ({ ...c, source: 'history' as const }))
}

export function mergeYahooAndHistoryCandles(
  yahoo: YahooDailyCandle[],
  historyCandles: TradeDailyCandle[]
): TradeDailyCandle[] {
  const map = new Map<string, TradeDailyCandle>()
  for (const c of yahoo) {
    map.set(dayKey(c.time), { ...c, source: 'yahoo' })
  }
  for (const c of historyCandles) {
    const key = dayKey(c.time)
    const existing = map.get(key)
    if (!existing) {
      map.set(key, c)
      continue
    }
    map.set(key, {
      time: existing.time,
      open: existing.open,
      high: Math.max(existing.high, c.high),
      low: Math.min(existing.low, c.low),
      close: c.source === 'history' && c.close ? c.close : existing.close,
      source: existing.source,
    })
  }
  return Array.from(map.values()).sort((a, b) => a.time - b.time)
}

export const CHART_PRE_ENTRY_DAYS = 10

/** Real session bars for the price chart. Flat gap-fills are kept only when no market bars exist. */
export function chartCandlesForDisplay<T extends TradeDailyCandle>(candles: T[]): T[] {
  const sorted = [...candles].sort((a, b) => a.time - b.time)
  const real = sorted.filter((c) => c.source === 'yahoo' || c.source === 'history')
  const chosen = real.length
    ? real
    : sorted.filter((c) => Number.isFinite(c.close) && c.close > 0)
  const byDay = new Map<string, T>()
  for (const candle of chosen) byDay.set(dayKey(candle.time), candle)
  return Array.from(byDay.values())
}

export function fillDailyGaps(
  candles: TradeDailyCandle[],
  rangeStart: number,
  rangeEnd: number,
  seedClose?: number | null
): TradeDailyCandle[] {
  const start = startOfUtcDay(rangeStart)
  const end = startOfUtcDay(rangeEnd)
  const seed =
    seedClose != null && Number.isFinite(seedClose) && seedClose > 0 ? seedClose : null

  if (!candles.length && seed != null) {
    const result: TradeDailyCandle[] = []
    for (let t = start; t <= end; t += 86400000) {
      result.push({
        time: t,
        open: seed,
        high: seed,
        low: seed,
        close: seed,
        source: 'synthetic',
      })
    }
    return result
  }

  if (!candles.length) return []

  const byDay = new Map(candles.map((c) => [dayKey(c.time), c]))
  const result: TradeDailyCandle[] = []
  let lastClose: number | null = seed

  for (let t = start; t <= end; t += 86400000) {
    const key = dayKey(t)
    const hit = byDay.get(key)
    if (hit) {
      result.push(hit)
      lastClose = hit.close
      continue
    }
    if (lastClose != null) {
      result.push({
        time: t,
        open: lastClose,
        high: lastClose,
        low: lastClose,
        close: lastClose,
        source: 'filled',
      })
    }
  }
  return result
}

function eventLabel(eventType: string, description: string): string {
  const labels: Record<string, string> = {
    entry: 'Entry',
    add: 'Add',
    reduce_size: 'Reduce',
    scale_in: 'Scale in',
    scale_out: 'Scale out',
    stop_move: 'Stop move',
    exit: 'Exit',
    note: 'Note',
  }
  if (labels[eventType]) return labels[eventType]
  return description?.slice(0, 40) || eventType.replace(/_/g, ' ')
}

export function buildHistoryAnnotations(
  trade: TradeForChart,
  history: HistoryForChart[],
  averageCost: number
): TradeHistoryAnnotation[] {
  const annotations: TradeHistoryAnnotation[] = []

  if (trade.entry_date) {
    annotations.push({
      time: parseTime(trade.entry_date),
      price: averageCost,
      title: 'Entry',
      description: 'Trade opened',
      eventType: 'entry',
    })
  }

  for (const e of history) {
    if (e.eventType === 'market_data' || e.tag === 'market_data') continue
    if (e.autoGenerated) continue
    const eventType = e.eventType || e.tag || 'event'
    annotations.push({
      id: e.id,
      time: parseTime(e.date),
      price: e.price ?? null,
      title: eventLabel(eventType, e.description || ''),
      description: e.description || e.title || eventType,
      eventType,
    })
  }

  if (trade.exit_date) {
    annotations.push({
      time: parseTime(trade.exit_date),
      price: trade.exit_price ?? null,
      title: 'Exit',
      description: 'Position closed',
      eventType: 'exit',
    })
  }

  return annotations.sort((a, b) => a.time - b.time)
}

type HistoryForChartWithAuto = HistoryForChart & { autoGenerated?: boolean }

function readStopFromMetadata(
  metadata?: HistoryForChart['metadata'],
  preferNew = false
): number | null {
  if (!metadata) return null
  const raw = preferNew
    ? metadata.new_stop_loss ?? metadata.stop_loss
    : metadata.stop_loss ?? metadata.new_stop_loss
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  return null
}

function eventKind(event: HistoryForChartWithAuto): string {
  return event.eventType || event.tag || ''
}

function notionals(price: number, quantity: number): number {
  return price * quantity
}

export function buildPositionRiskTimeline(
  trade: TradeForChart,
  history: HistoryForChartWithAuto[]
): PositionRiskPoint[] {
  const contract = trade.contract_size ?? 1
  let stopLoss = trade.stop_loss

  const events = history
    .filter((e) => e.eventType !== 'market_data' && e.tag !== 'market_data')
    .filter((e) => !e.autoGenerated)
    .sort((a, b) => parseTime(a.date) - parseTime(b.date))

  const isAdd = (kind: string) => kind === 'add' || kind === 'scale_in'
  const isReduce = (kind: string) => kind === 'reduce_size' || kind === 'scale_out'
  let addedQty = 0
  let reducedQty = 0
  let addedNotional = 0
  let reducedNotional = 0
  let hasEntryEvent = false
  for (const event of events) {
    const kind = eventKind(event)
    const eventQty = event.quantity ?? 0
    const price = event.price ?? trade.entry_price ?? 0
    if (kind === 'entry' && eventQty > 0) hasEntryEvent = true
    if (isAdd(kind) && eventQty > 0) {
      addedQty += eventQty
      addedNotional += notionals(price, eventQty)
    } else if (isReduce(kind) && eventQty > 0) {
      reducedQty += eventQty
      reducedNotional += notionals(price, eventQty)
    }
  }

  // trades.quantity and position_size are the current totals, after every add.
  // Replay from the size that existed at entry, then apply each event once.
  let qty = 0
  let positionSize = 0
  let cost = 0
  let avgCost = trade.entry_price ?? 0
  if (!hasEntryEvent) {
    qty = Math.max(0, (trade.quantity ?? 0) + reducedQty - addedQty)
    positionSize = Math.max(0, (trade.position_size ?? 0) + reducedNotional - addedNotional)
    cost = (trade.entry_price ?? 0) * qty
    avgCost = qty > 0 ? cost / qty : trade.entry_price ?? 0
  }

  const points: PositionRiskPoint[] = []

  const pushPoint = (time: number) => {
    const riskAmount = calcPositionRisk(avgCost, stopLoss, qty, contract, trade.long_short)
    const riskPct = calcPositionRiskPercent(
      avgCost,
      stopLoss,
      qty,
      contract,
      trade.long_short,
      positionSize > 0 ? positionSize : null
    )
    points.push({
      time,
      positionSize,
      riskPct,
      riskAmount,
      quantity: qty,
      avgCost,
      stopLoss,
    })
  }

  const entryTime = trade.entry_date ? parseTime(trade.entry_date) : Date.now()
  if (!hasEntryEvent) pushPoint(entryTime)

  for (const e of events) {
    const eventType = eventKind(e)
    const eventQty = e.quantity ?? 0
    const price = e.price ?? avgCost

    if (eventType === 'stop_move') {
      const nextStop = readStopFromMetadata(e.metadata, true)
      if (nextStop != null) stopLoss = nextStop
      pushPoint(parseTime(e.date))
      continue
    }

    if (eventType === 'entry' && eventQty > 0) {
      cost = notionals(price, eventQty)
      qty = eventQty
      avgCost = price
      positionSize = cost
      pushPoint(parseTime(e.date))
      continue
    }

    if (isAdd(eventType) && eventQty > 0) {
      cost += notionals(price, eventQty)
      qty += eventQty
      avgCost = qty > 0 ? cost / qty : avgCost
      positionSize += notionals(price, eventQty)
      pushPoint(parseTime(e.date))
      continue
    }

    if (isReduce(eventType) && eventQty > 0 && qty > 0) {
      const reduce = Math.min(eventQty, qty)
      const proportionalCost = (cost / qty) * reduce
      cost -= proportionalCost
      qty -= reduce
      positionSize = Math.max(0, positionSize - notionals(price, reduce))
      avgCost = qty > 0 ? cost / qty : avgCost
      pushPoint(parseTime(e.date))
      continue
    }

    if (eventType === 'exit') {
      qty = 0
      positionSize = 0
      pushPoint(parseTime(e.date))
      continue
    }

    if (eventType === 'note') {
      pushPoint(parseTime(e.date))
    }
  }

  const lastPoint = points[points.length - 1]
  if (!trade.exit_date && (!lastPoint || lastPoint.time < Date.now())) {
    pushPoint(Date.now())
  }

  const deduped: PositionRiskPoint[] = []
  for (const p of points) {
    const last = deduped[deduped.length - 1]
    if (last && last.time === p.time) deduped[deduped.length - 1] = p
    else deduped.push(p)
  }
  return deduped
}

export function buildTradeChartRows(
  candles: TradeDailyCandle[],
  riskTimeline: PositionRiskPoint[],
  annotations: TradeHistoryAnnotation[]
): TradeChartDayRow[] {
  const sortedRisk = [...riskTimeline].sort((a, b) => a.time - b.time)

  return candles.map((candle) => {
    const endOfDay = candle.time + 86400000 - 1
    let risk: PositionRiskPoint | null = null
    for (const point of sortedRisk) {
      if (point.time <= endOfDay) risk = point
    }
    const dayAnnotations = annotations.filter((a) => dayKey(a.time) === dayKey(candle.time))
    return {
      ...candle,
      positionSize: risk?.positionSize ?? null,
      riskPct: risk?.riskPct ?? null,
      annotations: dayAnnotations,
    }
  })
}

export function resolveChartRange(trade: TradeForChart): { start: number; end: number } {
  const entry = trade.entry_date ? parseTime(trade.entry_date) : Date.now() - 30 * 86400000
  const exit = trade.exit_date ? parseTime(trade.exit_date) : Date.now()
  const preEntry = CHART_PRE_ENTRY_DAYS * 86400000
  const postPad = 3 * 86400000
  return { start: startOfUtcDay(entry) - preEntry, end: startOfUtcDay(exit) + postPad }
}

export type PriceLevelSegment = {
  from: number
  to: number
  value: number
}

function readTakeProfitFromMetadata(metadata?: HistoryForChart['metadata']): number | null {
  if (!metadata) return null
  const raw = metadata.take_profit ?? metadata.new_take_profit
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  return null
}

function buildLevelSegments(
  initialValue: number,
  finalValue: number,
  trade: TradeForChart,
  history: HistoryForChart[],
  rangeEnd: number,
  kind: 'stop' | 'tp'
): PriceLevelSegment[] {
  const start = trade.entry_date ? parseTime(trade.entry_date) : rangeEnd - 30 * 86400000
  const end = trade.exit_date ? parseTime(trade.exit_date) : rangeEnd

  let entryLevel = initialValue
  if (kind === 'stop') {
    const firstMove = [...history]
      .filter((e) => e.eventType !== 'market_data' && e.tag !== 'market_data')
      .sort((a, b) => parseTime(a.date) - parseTime(b.date))
      .find((e) => (e.eventType || e.tag) === 'stop_move')
    const prev = firstMove?.metadata?.stop_loss
    if (typeof prev === 'number' && Number.isFinite(prev)) entryLevel = prev
  }

  const changes: { time: number; value: number }[] = [{ time: start, value: entryLevel }]

  const sorted = [...history]
    .filter((e) => e.eventType !== 'market_data' && e.tag !== 'market_data')
    .sort((a, b) => parseTime(a.date) - parseTime(b.date))

  for (const e of sorted) {
    const eventType = e.eventType || e.tag || ''
    if (kind === 'stop') {
      if (eventType === 'stop_move') {
        const next = readStopFromMetadata(e.metadata, true)
        if (next != null) changes.push({ time: parseTime(e.date), value: next })
      } else {
        const next = readStopFromMetadata(e.metadata)
        if (next != null) changes.push({ time: parseTime(e.date), value: next })
      }
    } else {
      const next = readTakeProfitFromMetadata(e.metadata)
      if (next != null) changes.push({ time: parseTime(e.date), value: next })
    }
  }

  const deduped: { time: number; value: number }[] = []
  for (const c of changes) {
    const prev = deduped[deduped.length - 1]
    if (prev && prev.time === c.time) deduped[deduped.length - 1] = c
    else if (!prev || prev.value !== c.value) deduped.push(c)
  }

  const segments: PriceLevelSegment[] = []
  for (let i = 0; i < deduped.length; i++) {
    const from = deduped[i].time
    const to = i < deduped.length - 1 ? deduped[i + 1].time : end
    const value =
      i === deduped.length - 1 && Number.isFinite(finalValue) ? finalValue : deduped[i].value
    if (to > from) {
      segments.push({ from, to, value })
    }
  }

  if (!segments.length && Number.isFinite(finalValue)) {
    segments.push({ from: start, to: end, value: finalValue })
  }

  return segments
}

export function buildStopTakeProfitSegments(
  trade: TradeForChart,
  history: HistoryForChart[],
  rangeEnd: number
): { stop: PriceLevelSegment[]; takeProfit: PriceLevelSegment[] } {
  return {
    stop: buildLevelSegments(trade.stop_loss, trade.stop_loss, trade, history, rangeEnd, 'stop'),
    takeProfit: buildLevelSegments(trade.take_profit, trade.take_profit, trade, history, rangeEnd, 'tp'),
  }
}
